import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// Technicians and subcontractors use the private field application at /technician.
const STAFF = ["admin", "office", "surveyor"];
const TECHNICIAN = ["technician", "subcontractor"];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, options = {}) => {
    const { data } = await api.post("/auth/login", {
      email,
      password,
      audience: options.audience || "customer",
      access_code: options.accessCode || "",
    });
    setUser(data);
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      setUser(false);
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    setUser,
    checkAuth,
    apiError,
    isStaff: !!user && STAFF.includes(user.role),
    isTechnician: !!user && TECHNICIAN.includes(user.role),
    isCustomer: !!user && user.role === "customer",
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
